import {Box} from '@mui/material';
import {usePersona} from 'src/permissions/hooks';
import {GridSortItem, GridSortModel} from '@mui/x-data-grid';
import {FC, useState} from 'react';
import {Table} from '../../tables/Table';
import {TableLayout} from '../../tables/TableLayout';
import {ContractsTableProps} from './ContractsTable.types';
import {columnConfigContracts} from './ContractsTable.tableConfig';
import {ContractFilterValue, ContractSearchRequestData} from 'src/types';
import {ContractSortFieldEnum} from 'src/enums';
import {ContractsTableActions} from '../ContractsTableActions';
import {useAppSelector, useBusinessId} from 'src/app/hooks';
import {ContractItemCard} from '../ContractItemCard';
import {ResponsiveTable} from '../../common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';

export const ContractsTable: FC<ContractsTableProps> = ({
                                                            data,
                                                            size,
                                                            pageNumber,
                                                            handleUpdateTableData,
                                                            handleChangePage,
                                                            handleChangeRowsNumber,
                                                            businessId,
                                                            title,
                                                            onAddClick,
                                                            buttonText,
                                                            onlyTitle,
                                                            icon,
                                                            filters,
                                                        }) => {
    // Seeded from the filter the page holds, not from empty: a remount is what coming back from a
    // detail page is, and starting at '' meant the next filter change wiped the search that was
    // applied - `updateData` writes `keyword: search`, so an empty local copy cleared the URL.
    const [searchValue, setSearchValue] = useState<string>(filters.keyword ?? '');
    const [sortValue, setSortValue] = useState<GridSortItem>({field: '', sort: 'desc'});


    const persona = usePersona();
    const {authInfo} = useAppSelector((state) => state.common);
    // The one the caller named, or the business being worked in when it named none.
    const activeBusinessId = useBusinessId();

    const handleSearch = (value: string) => {
        setSearchValue(value);
        handleUpdateTableData(updateData(value, sortValue, filters));
    };

    // The two fields this toolbar controls, merged onto the rest.
    const handleFilter = (value: ContractFilterValue) => {
        handleUpdateTableData(updateData(searchValue, sortValue, {
            ...filters,
            locationId: value.locationId,
            status: value.status,
        }));
    };

    const handleSort = (value: GridSortModel) => {
        setSortValue(value[0]);
        handleUpdateTableData(updateData(searchValue, value[0], filters));
    };

    /** A merge onto the filter the list is on, not a fresh object built from the toolbar's fields. */
    const updateData = (
        search: string,
        sort: GridSortItem,
        filter: ContractSearchRequestData
    ): ContractSearchRequestData => ({
        ...filter,
        keyword: search,
        sortAsc: sort ? sort.sort === 'asc' : true,
        sortField: (sort && sort.field) || ContractSortFieldEnum.CREATED,
    });

    const currentFilters: ContractSearchRequestData = {
        ...updateData(searchValue, sortValue, filters),
        businessId: businessId || activeBusinessId,
    };

    return (
        <Box>
            <TableLayout
                toolbar={(
                    <ContractsTableActions
                        title={title}
                        icon={icon}
                        onAdd={onlyTitle ? undefined : onAddClick}
                        addLabel={buttonText}
                        businessId={businessId}
                        handleSearch={handleSearch}
                        handleFilter={handleFilter}
                        filters={currentFilters}
                        totalElements={data.page.totalElements}
                    />
                )}
                totalPages={data.page.totalPages}
                page={pageNumber && pageNumber === 0 ? pageNumber : data.page.number}
                pageSize={size}
                onPageChange={handleChangePage}
                onPageSizeChange={handleChangeRowsNumber}
            >
            <ResponsiveTable
                responsiveElement={
                    <CardList>
                        {data?.content?.map((item) => (
                            <ContractItemCard
                                key={item?.id}
                                {...item}
                            />
                        ))}
                    </CardList>
                }
                desktopElement={
                    <Table
                        rows={data.content}
                        hideFooter
                        checkboxSelection={false}
                        columns={columnConfigContracts(persona.isPlatformAdmin, persona.isCompany)}
                        handleSort={handleSort}
                    />
                }
            />
            </TableLayout>
        </Box>
    );
};
